import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import {
  Trash2, Database, AlertCircle, CheckCircle2, Loader2, RotateCw,
  Square, ChevronDown, ChevronUp, ShieldCheck, FileWarning,
} from 'lucide-react';

/**
 * Storage Cleanup Card — Faz 5b (Şub 03, 2026)
 * --------------------------------------------------------------
 * S3'e taşınmış kayıtlardaki Mongo `file_content` alanlarını güvenli şekilde
 * temizler. Pre-flight S3 head_object + opsiyonel deep SHA-256 doğrulaması.
 */
const COLL_LABELS = {
  documents: 'Dökümanlar (Loonbrief)',
  vehicle_documents: 'Araç Dökümanları',
  vehicle_photos: 'Araç Fotoğrafları',
  vergunning_codes: 'Vergunning',
};

const STATUS_BADGES = {
  idle: { label: 'Boşta', cls: 'bg-slate-100 text-slate-600' },
  running: { label: 'Temizleniyor', cls: 'bg-rose-100 text-rose-700' },
  completed: { label: 'Tamamlandı', cls: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'İptal Edildi', cls: 'bg-amber-100 text-amber-700' },
  error: { label: 'Hata', cls: 'bg-rose-100 text-rose-700' },
};

const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export default function StorageCleanupCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deepVerify, setDeepVerify] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const pollRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/storage/cleanup/status');
      setData(r.data?.data || null);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (data?.status === 'running') {
      pollRef.current = setInterval(reload, 2000);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [data?.status, reload]);

  const handleStart = async () => {
    setBusy(true);
    try {
      await api.post(`/storage/cleanup/start?deep_verify=${deepVerify}`);
      toast.success('Temizlik başlatıldı');
      setConfirmOpen(false);
      await reload();
    } catch (e) {
      toast.error('Hata: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    try {
      await api.post('/storage/cleanup/cancel');
      toast.success('İptal isteği gönderildi');
      await reload();
    } catch (e) {
      toast.error('Hata: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <Card>
        <CardContent className="py-6 text-center text-sm text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Cleanup durumu yükleniyor…
        </CardContent>
      </Card>
    );
  }

  const status = data.status || 'idle';
  const badge = STATUS_BADGES[status] || STATUS_BADGES.idle;
  const cleanableTotal = data.cleanable_total || 0;
  const total = data.total || 0;
  const processed = data.processed || 0;
  const cleaned = data.cleaned || 0;
  const pct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const isRunning = status === 'running';

  return (
    <Card className="border-rose-200/60" data-testid="storage-cleanup-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-rose-500" /> Mongo Binary Temizliği
          </CardTitle>
          <Badge className={badge.cls}>{badge.label}</Badge>
        </div>
        <p className="text-xs text-slate-500">
          S3&apos;e taşınmış kayıtlardaki <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">file_content</code> alanını
          siler. <strong>Güvenli:</strong> her kayıt için önce S3&apos;te dosyanın varlığını doğrular, yoksa atlar.
          <strong> Geri alınamaz</strong> — sadece migration başarılı bittikten sonra çalıştırın.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pending breakdown */}
        {cleanableTotal > 0 && !isRunning && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(data.cleanable_by_collection || {}).map(([k, v]) => (
              <div key={k} className="border rounded p-2">
                <div className="text-[10px] uppercase text-slate-500 font-semibold truncate">{COLL_LABELS[k] || k}</div>
                <div className="text-xl font-bold tabular-nums text-rose-600">{v}</div>
                <div className="text-[10px] text-slate-400">binary&apos;de</div>
              </div>
            ))}
          </div>
        )}

        {/* Progress */}
        {isRunning && (
          <div className="space-y-2 bg-rose-50 dark:bg-rose-950/30 p-3 rounded-lg border border-rose-100">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                {data.current_collection ? `Aktif: ${COLL_LABELS[data.current_collection] || data.current_collection}` : 'Hazırlanıyor…'}
                {data.deep_verify && <Badge className="bg-amber-100 text-amber-700 text-[9px] ml-1">SHA-256 deep</Badge>}
              </span>
              <span className="font-mono tabular-nums">{processed} / {total} (%{pct})</span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="grid grid-cols-3 gap-2 text-[10px]">
              <div className="text-emerald-600"><CheckCircle2 className="h-3 w-3 inline" /> Temizlendi: <strong>{cleaned}</strong></div>
              <div className="text-amber-600">Atlandı: <strong>{data.skipped || 0}</strong></div>
              <div className="text-rose-600"><AlertCircle className="h-3 w-3 inline" /> Hatalı: <strong>{data.failed || 0}</strong></div>
            </div>
            <div className="text-[11px] text-slate-600">💾 Boşaltılan: <strong>{fmtBytes(data.bytes_freed)}</strong></div>
          </div>
        )}

        {/* Completed summary */}
        {(status === 'completed' || status === 'cancelled' || status === 'error') && total > 0 && (
          <div className={`p-3 rounded-lg border ${
            status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
            status === 'error' ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="text-xs font-semibold flex items-center gap-1">
              {status === 'completed' && <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Temizlik tamamlandı</>}
              {status === 'cancelled' && <><Square className="h-4 w-4 text-amber-600" /> Temizlik iptal edildi</>}
              {status === 'error' && <><AlertCircle className="h-4 w-4 text-rose-600" /> Temizlik hatayla bitti</>}
            </div>
            <div className="text-[11px] mt-1 text-slate-600">
              {processed} işlendi · {cleaned} temizlendi · {data.skipped || 0} atlandı · {data.failed || 0} hatalı
            </div>
            <div className="text-[11px] mt-0.5 text-slate-700 font-semibold">
              💾 Toplam boşaltılan: {fmtBytes(data.bytes_freed)}
            </div>
            {data.error_message && (
              <div className="text-xs text-rose-700 mt-2 font-mono">{data.error_message}</div>
            )}
          </div>
        )}

        {/* Actions */}
        {!isRunning && cleanableTotal === 0 && (
          <div className="text-xs text-emerald-600 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> Temizlenecek kayıt yok — Mongo zaten temiz.
          </div>
        )}

        {!isRunning && cleanableTotal > 0 && !confirmOpen && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setConfirmOpen(true)} variant="outline" className="text-rose-600 border-rose-300"
                    data-testid="cleanup-show-confirm-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Temizliği Başlat ({cleanableTotal} kayıt)
            </Button>
            <Button variant="ghost" size="sm" onClick={reload}>
              <RotateCw className="h-4 w-4 mr-1" /> Yenile
            </Button>
          </div>
        )}

        {confirmOpen && !isRunning && (
          <div className="border-2 border-rose-200 bg-rose-50/50 rounded-lg p-3 space-y-3">
            <div className="text-sm font-semibold text-rose-700 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" /> Onaylıyor musunuz?
            </div>
            <p className="text-xs text-slate-600">
              <strong>{cleanableTotal} kayıt</strong> içindeki Mongo binary alanları silinecek.
              Bu işlem geri alınamaz. Dosyalara her zaman S3/CloudFront üzerinden erişilecek.
            </p>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <Checkbox checked={deepVerify} onCheckedChange={setDeepVerify} className="mt-0.5" />
              <span>
                <strong className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-600" /> Derin doğrulama (deep verify)</strong>
                <div className="text-slate-500 mt-0.5">
                  Her dosya için S3&apos;ten indirip SHA-256 karşılaştırır. <strong>Çok daha yavaş</strong>,
                  ama mutlak güvenlik sağlar. (Varsayılan: sadece head_object hızlı kontrol.)
                </div>
              </span>
            </label>
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}>İptal</Button>
              <Button size="sm" onClick={handleStart} disabled={busy} className="bg-rose-600 hover:bg-rose-700"
                      data-testid="cleanup-confirm-btn">
                {busy && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Evet, Şimdi Temizle
              </Button>
            </div>
          </div>
        )}

        {isRunning && (
          <Button variant="outline" size="sm" onClick={handleCancel} disabled={busy} className="text-rose-600">
            <Square className="h-4 w-4 mr-1" /> İptal
          </Button>
        )}

        {/* Errors */}
        {Array.isArray(data.errors) && data.errors.length > 0 && (
          <div className="border-t pt-3">
            <button onClick={() => setErrorsOpen(!errorsOpen)}
                    className="text-xs font-semibold text-rose-600 flex items-center gap-1 hover:underline">
              <FileWarning className="h-3 w-3" /> Atlanan/Hatalı ({data.errors.length})
              {errorsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {errorsOpen && (
              <div className="mt-2 max-h-48 overflow-y-auto text-[11px] space-y-1 font-mono bg-slate-50 dark:bg-slate-900 rounded p-2">
                {data.errors.slice().reverse().map((e, i) => (
                  <div key={i} className="border-b border-slate-200 dark:border-slate-700 py-1 last:border-0">
                    <span className="text-slate-400">[{e.collection}]</span>{' '}
                    <span className="text-slate-600">{e.doc_id?.slice(0, 8)}</span>:{' '}
                    <span className="text-amber-700">{e.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
